import React, { useEffect, useRef, useState } from 'react';
import './codeMirrorStyle.css';

const StackTraceViewer = ({ value }) => {
  const editorContainerRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    if (editorContainerRef.current && !editorInstanceRef.current && !useFallback) {
      let mounted = true;
      Promise.all([
        import('@codemirror/view'),
        import('@codemirror/state'),
        import('@codemirror/language'),
        import('@codemirror/lang-javascript'),
      ])
        .then(([cmView, cmState, cmLang, cmJs]) => {
          if (!mounted || !editorContainerRef.current) return;
          try {
            const { EditorView, lineNumbers, highlightSpecialChars, drawSelection } = cmView;
            const { EditorState } = cmState;
            const { syntaxHighlighting, defaultHighlightStyle } = cmLang;
            const { javascript } = cmJs;
            const state = EditorState.create({
              doc: value || '',
              extensions: [
                lineNumbers(),
                highlightSpecialChars(),
                drawSelection(),
                syntaxHighlighting(defaultHighlightStyle),
                javascript(),
                EditorView.lineWrapping,
                EditorState.readOnly.of(true),
                EditorView.editable.of(false),
              ],
            });
            editorInstanceRef.current = new EditorView({
              state,
              parent: editorContainerRef.current,
            });
          } catch (err) {
            console.warn('[AIRca] CodeMirror init failed:', err);
            setUseFallback(true);
          }
        })
        .catch((err) => {
          console.warn('[AIRca] CodeMirror import failed:', err);
          if (mounted) setUseFallback(true);
        });

      return () => {
        mounted = false;
        if (editorInstanceRef.current) {
          editorInstanceRef.current.destroy();
          editorInstanceRef.current = null;
        }
      };
    }
  }, [useFallback]);

  useEffect(() => {
    if (
      editorInstanceRef.current &&
      value !== editorInstanceRef.current.state.doc.toString()
    ) {
      try {
        const transaction = editorInstanceRef.current.state.update({
          changes: {
            from: 0,
            to: editorInstanceRef.current.state.doc.length,
            insert: value || '',
          },
        });
        editorInstanceRef.current.update([transaction]);
      } catch {
        // Ignore update errors
      }
    }
  }, [value]);

  if (useFallback) {
    return (
      <pre className="airca-codeblock-fallback">{value || ''}</pre>
    );
  }

  return <div ref={editorContainerRef} className="airca-codeblock-editor" />;
};

export default StackTraceViewer;
